import React from 'react';
import { Box, Card, Container, Flex, HStack, SimpleGrid, Stack, VStack } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { Logo } from '@/components/chakraUIPro/Logo';
import { Skeleton, SkeletonCircle } from '@/components/ui/skeleton';
import { InvoiceHistoryChartSkeleton } from '@/components/charts/charts/InvoiceHistoryChartSkeleton';
import { AccessPageSkeleton, OrganisationSettingsSkeleton } from '@/components/Dashboard/Settings/SettingsSkeletons';
import {
  DEFAULT_TAB,
  parseSectionFromLocation,
  parseSectionFromPath,
  type RoutableTabKey
} from '@/components/Dashboard/dashboardSections';

type DashboardLoadingSection = typeof DEFAULT_TAB | RoutableTabKey | null;

interface DashboardTabsLoadingShellProps {
  contentTopOffset?: string;
  section?: DashboardLoadingSection;
  children?: React.ReactNode;
}

const getQueryStringValue = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value;

const getDashboardSectionFromPath = (
  path: string | undefined,
  allowDefaultDashboard: boolean
): DashboardLoadingSection => {
  if (!path) return null;

  const segments = path.split('?')[0].split('#')[0].split('/').filter(Boolean);
  if (segments[0] !== 'dashboard') return null;

  const section = parseSectionFromPath(path);
  if (section) return section;

  return allowDefaultDashboard && segments.length <= 2 ? DEFAULT_TAB : null;
};

const getBrowserDashboardSection = (): DashboardLoadingSection => {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const redirectedPath = params.get('path') ?? params.get('page') ?? undefined;

  return getDashboardSectionFromPath(redirectedPath ?? window.location.pathname, true);
};

export const DashboardContentLoadingShell = () => (
  <Box>
    <Box as='section' pb={{ base: '4', md: '6' }} mt={10}>
      <Flex direction={{ base: 'column', md: 'row' }} justifyContent='space-between' my={10}>
        <HStack gap='3' mr={6} mb={{ base: 6, md: 0 }}>
          <Skeleton height='24px' width='48px' borderRadius='full' />
          <Skeleton height='14px' width='80px' />
        </HStack>
        <Stack gap={6} direction={{ base: 'column', md: 'row' }}>
          <Skeleton height='40px' width='196px' borderRadius='md' />
          <Skeleton height='40px' width='153px' borderRadius='md' />
          <Skeleton height='40px' width='106px' borderRadius='md' />
        </Stack>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }} gap={{ base: '5', md: '6' }}>
        {[0, 1].map((item) => (
          <Card.Root key={item}>
            <Card.Body gap='2'>
              <Skeleton height='4' width='120px' />
              <Skeleton height='8' width='48px' />
              <Skeleton height='4' width='96px' />
            </Card.Body>
          </Card.Root>
        ))}
      </SimpleGrid>
    </Box>

    <VStack gap={12} my='6'>
      <InvoiceHistoryChartSkeleton />

      <Card.Root w='full' minH='368px' mb={6}>
        <Card.Header flexDirection={{ base: 'column', md: 'row' }} alignItems='center' justifyContent='space-between'>
          <Skeleton height='24px' width='160px' />
          <HStack gap='2' display={{ base: 'none', md: 'flex' }}>
            <Skeleton height='40px' width='40px' borderRadius='md' />
            <Skeleton height='40px' width='152px' borderRadius='md' />
            <Skeleton height='40px' width='132px' borderRadius='md' />
          </HStack>
        </Card.Header>
        <Card.Body>
          <Box mt='4'>
            <Box py='2'>
              <Stack direction={{ base: 'column', md: 'row' }} gap='2'>
                <Skeleton height='44px' width='104px' borderRadius='md' />
                <Skeleton height='44px' flex='1' borderRadius='md' />
                <Skeleton height='44px' width='104px' borderRadius='md' display={{ base: 'none', md: 'block' }} />
              </Stack>
            </Box>
            <Flex justifyContent='flex-end' my='4'>
              <Skeleton height='20px' width='92px' />
            </Flex>
            <Stack gap='0' mt='2'>
              {[0, 1, 2].map((item) => (
                <Skeleton key={item} height={item === 0 ? '42px' : '40px'} />
              ))}
            </Stack>
          </Box>
        </Card.Body>
      </Card.Root>
    </VStack>
  </Box>
);

const DashboardSectionLoadingShell = ({ section }: { section?: DashboardLoadingSection }) => {
  if (section === 'settings') {
    return <OrganisationSettingsSkeleton />;
  }
  if (section === 'access') {
    return <AccessPageSkeleton />;
  }
  if (section === DEFAULT_TAB) {
    return <DashboardContentLoadingShell />;
  }

  return (
    <Flex minH='420px' align='center' justify='center'>
      <Stack align='center' gap='4' w='full' maxW='320px'>
        <SkeletonCircle size='12' />
        <Skeleton height='18px' width='220px' />
        <Skeleton height='14px' width='160px' />
      </Stack>
    </Flex>
  );
};

export const DashboardTabsLoadingShell = ({
  contentTopOffset = '18px',
  section = null,
  children
}: DashboardTabsLoadingShellProps) => (
  <Box aria-busy='true'>
    <HStack gap={{ base: '6', md: '10' }} borderBottomWidth='1px' pb='4' overflow='hidden'>
      <Skeleton height='28px' width='77px' />
      <Skeleton height='28px' width='58px' />
      <Skeleton height='28px' width='49px' display={{ base: 'none', md: 'block' }} />
    </HStack>

    <Box pt={contentTopOffset}>
      {children || <DashboardSectionLoadingShell section={section} />}
    </Box>
  </Box>
);

export const DashboardRouteLoadingShell = () => {
  const router = useRouter();
  const redirectedPath = getQueryStringValue(router.query.path) ?? getQueryStringValue(router.query.page);
  const routerSection =
    parseSectionFromLocation(router.query.location) ??
    getDashboardSectionFromPath(redirectedPath, true) ??
    getDashboardSectionFromPath(router.asPath, router.isReady);
  const browserSection = getBrowserDashboardSection();
  const section = router.isReady ? routerSection ?? browserSection : browserSection ?? routerSection;

  return <DashboardTabsLoadingShell section={section} />;
};

export const AppRouteLoadingShell = () => (
  <Box minH='100vh' bg='bg-default' aria-busy='true'>
    <Container py={{ base: '3', lg: '4' }} maxWidth='8xl'>
      <Flex justify='space-between' align='center' gap='4'>
        <HStack gap='4'>
          <Logo />
          <Skeleton height='44px' width={{ base: '145px', md: '180px' }} borderRadius='md' />
        </HStack>
        <HStack gap='0' display={{ base: 'none', md: 'flex' }}>
          <Skeleton height='40px' width='64px' />
          <Skeleton height='40px' width='106px' />
          <Skeleton height='40px' width='54px' />
          <SkeletonCircle size='44px' />
        </HStack>
      </Flex>
    </Container>

    <Container pt={{ base: '8', lg: '8' }} pb={{ base: '12', lg: '24' }} maxWidth='8xl'>
      <DashboardRouteLoadingShell />
    </Container>
  </Box>
);
